import * as React from 'react';
import JSONbig from "json-bigint";
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Stack from "@mui/joy/Stack";
import CircularProgress from '@mui/joy/CircularProgress';
import IconButton, {iconButtonClasses} from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Avatar from "@mui/joy/Avatar";
import { formatDateTime } from '../../common/dateTime';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import { formatRubles } from "../../common/money";
import PauseIcon from '@mui/icons-material/Pause';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import {Game} from "../../models/games";
import {gamesListApi} from "../games/gamesApi";
import {Bid} from "../../models/bid";
import {bidsListApi} from "./bidsApi";
import {NotificationService} from "../../common/notification";
import {useSearchParams} from "react-router-dom";
import Link from '@mui/joy/Link';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import ListDivider from '@mui/joy/ListDivider';
import DebounceInput from '../../components/debounceInput/DebounceInput';
import {getPages} from "../../common/pages/pages";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {BidHistoryModal} from "./BidHistoryModal";

const JSONbigNative = JSONbig({ useNativeBigInt: true });

type DefaultProps = {
    userId?: bigint
}

function parseBidIdParam(raw: string | null): number {
    if (!raw) {
        return -1;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : -1;
}

function parseUserIdParam(raw: string | null, fallback?: bigint): bigint {
    if (fallback !== undefined) {
        return fallback;
    }
    if (!raw) {
        return BigInt(-1);
    }
    try {
        return JSONbigNative.parse(raw) as bigint;
    } catch {
        return BigInt(-1);
    }
}

export default function BidsTable(props: DefaultProps) {
    const {userId} = props;

    const [searchParams] = useSearchParams();
    const [games, setGames] = React.useState<Game[]>([]);
    const [loadedGames, setLoadedGames] = React.useState(false);
    const [bids, setBids] = React.useState<Bid[]>([]);
    const [isDataLoading, setIsDataLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [filterUser, setFilterUser] = React.useState<bigint>(() =>
        parseUserIdParam(searchParams.get("userId"), userId)
    );
    const [filterGame, setFilterGame] = React.useState<number>(() => {
        const raw = searchParams.get("gameId");
        if (!raw) {
            return -1;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? n : -1;
    });
    const [filterHashName, setFilterHashName] = React.useState(() => searchParams.get("hashName") ?? "");
    const [filterBidId, setFilterBidId] = React.useState(() => parseBidIdParam(searchParams.get("bidId")));
    const [filterState, setFilterState] = React.useState<number>(-1);
    const [filtersModalOpen, setfiltersModalOpen] = React.useState(false);
    const [historyBidId, setHistoryBidId] = React.useState<number | null>(null);
    const closeHistory = React.useCallback(() => setHistoryBidId(null), []);

    const userIdFromUrl = searchParams.get("userId");
    const hashNameFromUrl = searchParams.get("hashName") ?? "";
    const gameIdFromUrl = searchParams.get("gameId");
    const bidIdFromUrl = searchParams.get("bidId");
    const skipPageResetOnUrlSync = React.useRef(true);

    React.useEffect(() => {
        const nextUser = parseUserIdParam(userIdFromUrl, userId);
        const nextHash = hashNameFromUrl;
        const nextGame = gameIdFromUrl != null && gameIdFromUrl !== "" && Number.isFinite(Number(gameIdFromUrl))
            ? Number(gameIdFromUrl)
            : -1;
        const nextBidId = parseBidIdParam(bidIdFromUrl);

        setFilterUser((prev) => (prev !== nextUser ? nextUser : prev));
        setFilterHashName((prev) => (prev !== nextHash ? nextHash : prev));
        setFilterBidId((prev) => (prev !== nextBidId ? nextBidId : prev));
        if (gameIdFromUrl != null) {
            setFilterGame((prev) => (prev !== nextGame ? nextGame : prev));
        }

        if (skipPageResetOnUrlSync.current) {
            skipPageResetOnUrlSync.current = false;
            return;
        }
        setPage(1);
    }, [userIdFromUrl, hashNameFromUrl, gameIdFromUrl, bidIdFromUrl, userId]);

    React.useEffect(() => {
        loadGames().catch(console.error);
    }, [userId])

    React.useEffect(() => {
        if (!filtersModalOpen) {
            loadData().catch(console.error);
        }
    }, [page, filterGame, filterUser, filterHashName, filterBidId, filterState, filtersModalOpen])

    const renderFilters = () => {
        return (
            <React.Fragment>
                {!loadedGames ?
                    <CircularProgress/>
                    :
                    <FormControl size="sm">
                        <FormLabel>Игра</FormLabel>
                        <Select size="sm"
                                placeholder="Игра"
                                startDecorator={<VideogameAssetIcon/>}
                                value={filterGame}
                                onChange={(_, value) => setFilterGame(value ?? -1)}>
                            <Option key={-1} value={-1}>Все</Option>
                            {games.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                        </Select>
                    </FormControl>
                }
                <FormControl size="sm">
                    <FormLabel>Статус</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Статус"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={filterState}
                        onChange={(_, value) => setFilterState(value ?? -1)}
                    >
                        <Option value={-1}>Все</Option>
                        <Option value={1}>ТолькоАктивные</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>ID заявки</FormLabel>
                    <DebounceInput
                        key={`bid-${filterBidId}`}
                        size="sm"
                        placeholder="ID заявки"
                        debounceTimeout={500}
                        defaultValue={filterBidId > 0 ? filterBidId.toString() : ""}
                        handleDebounce={(val) => setFilterBidId(parseBidIdParam(val))}
                    />
                </FormControl>
                {userId === undefined
                ? <FormControl size="sm">
                    <FormLabel>Пользователь (Steam ID)</FormLabel>
                    <DebounceInput
                        key={`user-${filterUser.toString()}`}
                        size="sm"
                        placeholder="Пользователь"
                        debounceTimeout={500}
                        defaultValue={filterUser > -1 ? filterUser.toString() : ""}
                        handleDebounce={(val) => setFilterUser(JSONbigNative.parse(val || '-1'))}
                    />
                </FormControl>
                : <></>}
            </React.Fragment>
        );
    }

    const applyFilter = async () => {
        setfiltersModalOpen(false)
        if (page !== 1) {
            setPage(1);
        }
    }

    const loadGames = async () => {
        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedGames(true)
        }
    }

    const loadData = async () => {
        setIsDataLoading(true)
        try {
            let result = await bidsListApi(page, 50, filterGame, filterUser, filterState === 1 ? true : false, filterHashName, filterBidId);
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.max(1, Math.ceil(res.total / 50)));
                setBids(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setIsDataLoading(false)
        }
    }

    const getStateIcon = (state: boolean): any => state ? (<HourglassBottomIcon/>) : (<PauseIcon/>);
    const getStateColor = (state: boolean): "danger" | "success" => state ? 'success': 'danger';
    const getStateDescription = (bid: Bid): string => bid.isActive ? 'В обработке' : 'Приостановлено';

    return (
            <div>
                {/* Фильтра для мобилок */}
                <Sheet
                    className="SearchAndFilters-mobile"
                    sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
                >
                    <DebounceInput
                        key={`hash-mobile-${filterHashName}`}
                        size="sm"
                        placeholder="Имя скина"
                        startDecorator={<SearchIcon/>}
                        sx={{flexGrow: 1}}
                        debounceTimeout={1000}
                        defaultValue={filterHashName}
                        handleDebounce={(val) => setFilterHashName(val)}
                    />
                    <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        onClick={() => setfiltersModalOpen(true)}
                    >
                        <FilterAltIcon/>
                    </IconButton>
                    <Modal open={filtersModalOpen} onClose={() => setfiltersModalOpen(false)}>
                        <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                            <ModalClose/>
                            <Typography id="filter-modal" level="h2">
                                Фильтры
                            </Typography>
                            <Divider sx={{my: 2}}/>
                            <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                {renderFilters()}
                                <Button color="primary" onClick={applyFilter}>
                                    Применить
                                </Button>
                                <Button color="neutral" onClick={() => setfiltersModalOpen(false)}>
                                    Закрыть
                                </Button>
                            </Sheet>
                        </ModalDialog>
                    </Modal>
                </Sheet>
                {/* Фильтра для десктопа */}
                <Box
                    className="SearchAndFilters-tabletUp"
                    sx={{
                        borderRadius: 'sm',
                        py: 2,
                        display: {xs: 'none', sm: 'flex'},
                        flexWrap: 'wrap',
                        gap: 1.5,
                        '& > *': {
                            minWidth: {xs: '120px', md: '160px'},
                        },
                    }}
                >
                    <FormControl size="sm">
                        <FormLabel>Наименование скина</FormLabel>
                        <DebounceInput
                            key={`hash-desktop-${filterHashName}`}
                            size="sm"
                            placeholder="Имя"
                            startDecorator={<SearchIcon/>}
                            debounceTimeout={1000}
                            defaultValue={filterHashName}
                            handleDebounce={(val) => setFilterHashName(val)}
                        />
                    </FormControl>
                    {renderFilters()}
                </Box>
                {isDataLoading ?
                    <CircularProgress/>
                    :
                    <>
                    {/* Таблица  десктопа */}
                    <Sheet
                        className="BidsTableContainer"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 140, padding: '12px 6px'}}>#</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Скин</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Игра</th>
                                {userId === undefined
                                ?<th style={{width: 180, padding: '12px 6px'}}>Покупатель</th>
                                :<></>}
                                <th style={{width: 140, padding: '12px 6px'}}>Цена</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Кол-во</th>
                                <th style={{width: 70, padding: '12px 6px'}}>В резерве</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Куплено</th>
                                <th style={{width: 200, padding: '12px 6px'}}>Фильтр</th>
                                <th style={{width: 120, padding: '12px 6px'}}>Статус</th>
                                <th style={{width: 40, padding: '12px 6px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {bids.map((row) => {
                                return (
                                <tr key={row.bidId}>
                                    <td>
                                        <Stack spacing={0.5} direction={'column'}>
                                            <Typography level="body-xs">{row.bidId}</Typography>
                                            <Typography level="body-xs">от {formatDateTime(row.createdAt)}</Typography>
                                            <Typography level="body-xs">Очередность - {row.bidPosition}</Typography>
                                        </Stack>
                                    </td>
                                    <td>
                                        <Tooltip title={row.hashName}>
                                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                                <Avatar
                                                    variant="outlined"
                                                    size="sm"
                                                    src={row.image}
                                                />
                                                <Stack spacing={0.5} direction={'column'}>
                                                    <Link
                                                        underline="hover"
                                                        href={`https://steamcommunity.com/market/search?q=${row.hashName}`}
                                                        target="_blank"
                                                        level="body-xs"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: '2',
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {row.hashName}
                                                    </Link>
                                                    <Typography level="body-xs">Мин.цена - {formatRubles(row.minSellPrice)}</Typography>
                                                </Stack>
                                            </Stack>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.gameName}</Typography>
                                    </td>
                                    {userId === undefined
                                    ?<td>
                                        <Stack spacing={1} direction={'column'}>
                                            <Link
                                                underline="hover"
                                                href={`/app/users/${row.buyerId.toString()}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                level="body-xs"
                                            >
                                                {row.buyerId.toString()}
                                            </Link>
                                            <Link
                                                underline="hover"
                                                href={`https://steamcommunity.com/profiles/${row.buyerId.toString()}/`}
                                                target="_blank"
                                                level="body-xs"
                                            >
                                                (Steam - {row.buyerName})
                                            </Link>
                                        </Stack>
                                    </td>
                                    :<></>}
                                    <td>
                                        <Stack spacing={0.5} direction={'column'}>
                                            <Typography level="body-xs">{formatRubles(row.price)}</Typography>
                                            <Typography level="body-xs">от {formatDateTime(row.priceUpdatedAt)}</Typography>                                            
                                            <Typography level="body-xs">Макс.цена - {formatRubles(row.maxBidPrice)}</Typography>
                                        </Stack>
                                    </td>
                                    <td>
                                        <Tooltip title="Общее требуемое количество по заявке">
                                            <Typography level="body-xs">{row.count}</Typography>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Tooltip title="Количество заказов по заявке в обработке">
                                            <Typography level="body-xs">{row.reserved === -1 ? '-' : row.reserved}</Typography>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Tooltip title="Уже купленное количество по заявке">
                                            {row.bought > 0
                                            ?
                                            (<Link
                                                underline="hover"
                                                href={`/app/orders?bidId=${row.bidId}&status=5`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                level="body-xs"
                                            >
                                               {row.bought} (ордера)
                                            </Link>)
                                            :
                                            <>-</>}
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <Stack spacing={1} direction={'column'}>
                                            {row.descriptions?.map((p) => (
                                                <Typography><b>{p.title}</b>: {p.description}</Typography>))}
                                        </Stack>
                                    </td>
                                    <td>
                                        <Chip
                                            variant="soft"
                                            size="sm"
                                            startDecorator={getStateIcon(row.isActive)}
                                            color={getStateColor(row.isActive)}
                                        >
                                            {getStateDescription(row)}
                                        </Chip>
                                    </td>
                                    <td>
                                        <Tooltip title="История заявки">
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                onClick={() => setHistoryBidId(row.bidId)}
                                            >
                                                <InfoOutlinedIcon/>
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>);
                            })}
                            </tbody>
                        </Table>
                    </Sheet>

                    {/* Список для мобилок */}
                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {bids.map((listItem) => {
                            return (<List key={listItem.bidId} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                >
                                    <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <div>
                                            <Chip
                                                variant="soft"
                                                size="sm"
                                                startDecorator={getStateIcon(listItem.isActive)}
                                                color={getStateColor(listItem.isActive)}
                                            >
                                                {getStateDescription(listItem)}
                                            </Chip>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography gutterBottom sx={{fontWeight: 600}}>
                                                    #{listItem.bidId}, от {formatDateTime(listItem.createdAt)}
                                                </Typography>
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    onClick={() => setHistoryBidId(listItem.bidId)}
                                                >
                                                    <InfoOutlinedIcon/>
                                                </IconButton>
                                            </Stack>
                                            <Typography level="body-xs">Очередность - {listItem.bidPosition}
                                            </Typography>
                                            <Stack spacing={1} direction={'row'}>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/market/search?q=${listItem.hashName}`}
                                                    target="_blank"
                                                    level="body-xs"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: '2',
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {listItem.hashName}
                                                </Link>
                                                <Typography level="body-xs">(Мин.цена - {formatRubles(listItem.minSellPrice)})</Typography>
                                            </Stack>
                                            <Typography level="body-xs">Игра: {listItem.gameName}</Typography>
                                            {userId === undefined
                                            ? <Stack spacing={1} direction={'row'} alignItems={'center'}>
                                                <Typography level="body-xs">Покупатель:</Typography>
                                                <Link
                                                    underline="hover"
                                                    href={`/app/users/${listItem.buyerId.toString()}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    level="body-xs"
                                                >
                                                    {listItem.buyerId.toString()}
                                                </Link>
                                                <Link
                                                    underline="hover"
                                                    href={`https://steamcommunity.com/profiles/${listItem.buyerId.toString()}/`}
                                                    target="_blank"
                                                    level="body-xs"
                                                >
                                                    ({listItem.buyerName})
                                                </Link>
                                            </Stack> : <></>}
                                            <Typography level="body-xs">Цена: {formatRubles(listItem.price)} от {formatDateTime(listItem.priceUpdatedAt)}</Typography>
                                            <Typography level="body-xs">Макс.цена: {formatRubles(listItem.maxBidPrice)}</Typography>
                                            {listItem.descriptions?.map((p) => (<Typography level="body-xs">{p.title}: {p.description}</Typography>))}
                                            <Stack spacing={1} direction={'row'}>
                                                <Typography level="body-xs">Всего/Заказано/Куплено: {listItem.count}/{listItem.reserved === -1 ? '-' : listItem.reserved}/{listItem.bought}</Typography>
                                                {listItem.bought > 0
                                                ? <Link underline="hover"
                                                        href={`/app/orders?bidId=${listItem.bidId}&status=5`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        level="body-xs"
                                                    >
                                                        (заказы)
                                                    </Link>
                                                : <></>}
                                            </Stack>
                                        </div>
                                    </ListItemContent>
                                    {listItem.image
                                    ? <Box component="img" src={listItem.image} sx={{ height: "30px", width: "auto" }} />
                                    : <></>}
                                </ListItem>
                                <ListDivider/>
                            </List>)
                        })}
                        <Box
                            className="Pagination-mobile"
                            sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                        >
                            <IconButton
                                aria-label="previous page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => setPage(state => state - 1)}
                            >
                                <KeyboardArrowLeftIcon/>
                            </IconButton>
                            <Typography level="body-sm" sx={{mx: 'auto'}}>
                                Страница {page} из {pageCount}
                            </Typography>
                            <IconButton
                                aria-label="next page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page >= pageCount}
                                onClick={() => setPage(state => state + 1)}
                            >
                                <KeyboardArrowRightIcon/>
                            </IconButton>
                        </Box>
                    </Box>
                    </>
                }
                {/* Paging для десктопа */}
                <Box
                    className="Pagination-laptopUp"
                    sx={{
                        pt: 2,
                        gap: 1,
                        [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                        display: {
                            xs: 'none',
                            md: 'flex',
                        },
                    }}
                >
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<KeyboardArrowLeftIcon/>}
                        disabled={page <= 1}
                        onClick={() => setPage(state => state - 1)}
                    >
                        Назад
                    </Button>
                    <Box sx={{flex: 1}}/>
                    {getPages(pageCount).map((p) => (
                        <IconButton
                            key={p}
                            size="sm"
                            variant={Number(p) ? 'outlined' : 'plain'}
                            color={page === Number(p) ? "primary" : "neutral"}
                            onClick={() => setPage(Number(p))}
                        >
                            {p}
                        </IconButton>
                    ))}
                    <Box sx={{flex: 1}}/>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        endDecorator={<KeyboardArrowRightIcon/>}
                        disabled={page >= pageCount}
                        onClick={() => setPage(state => state + 1)}
                    >
                        Дальше
                    </Button>
                </Box>
                <BidHistoryModal bidId={historyBidId} onClose={closeHistory} />
            </div>
    );
}